import { ReactNode } from "react";
import Answered from "@/components/Answered";
import ChatBubble from "@/components/ChatBubble";
import Page from "@/components/Page";
import Visible from "@/components/Visible";
import styles from "@/pages/Pages.module.scss";

type Props = {
  children?: ReactNode;
  variant?: "A" | "B";
};

export default function ObesityRecommendationContent({
  children,
  variant = "A",
}: Props) {
  const existingTreatmentQuestionId = "q5";

  return (
    <Page>
      <div className="mb-30">
        <ChatBubble>
          Hej, jag heter Qim och jag är verksamhets&shy;chef på
          Blodtrycksdoktorn.
        </ChatBubble>
      </div>

      <Answered affirmative to={existingTreatmentQuestionId}>
        <Visible when={variant === "A"}>
          <ExistingTreatmentInfoVariantA />
        </Visible>
        <Visible when={variant === "B"}>
          <ExistingTreatmentInfoVariantB />
        </Visible>
      </Answered>

      <Answered negative to={existingTreatmentQuestionId}>
        <NewTreatmentInfo />
      </Answered>

      {children}

      <div className={styles.additionalInfo}>
        <strong>Tänk på att</strong>
        <ul>
          <li>Kostnader för läkemedel tillkommer</li>
          <li>
            Vår överviktsbehandling är idag inte utformad för att hjälpa
            personer med hjärt-njursvikt, inflammatorisk tarmsjukdom, pågående
            cancerbehandling, insulinbehandlad diabetes eller under tiden som du
            är gravid.
          </li>
        </ul>
      </div>
    </Page>
  );
}

const NewTreatmentInfo = () => {
  return (
    <>
      <h1>Ett steg kvar till din kostnadsfria bedömning</h1>

      <h3>Så funkar det:</h3>

      <ol>
        <li>
          <strong>Du legitimerar dig med BankID</strong> för att skicka in dina
          svar
        </li>
        <li>
          <strong>Du laddar ner appen och loggar in</strong> – dina svar finns
          redan där
        </li>
        <li>
          <strong>Du får en kostnadsfri bedömning</strong> där du kartlägger din
          hälsosituation och får en bedömning av din sjuksköterska om vi kan
          hjälpa dig.
        </li>
        <li>
          <strong>Om behandling är rätt</strong> för dig gör vi en utredning.{" "}
          <a href="https://blodtrycksdoktorn.se/priser/" target="_blank">
            Kostnad för utredning tillkommer innan påbörjad behandling
          </a>
          .
        </li>
      </ol>

      <p>
        Blodtrycksdoktorn är en registrerad svensk vårdgivare. BankID krävs för
        trygg och säker identifiering.
      </p>
    </>
  );
};

const ExistingTreatmentInfoVariantA = () => {
  return (
    <>
      <h1>Ett steg kvar till din kostnadsfria bedömning</h1>

      <h3>Så funkar det:</h3>

      <ol>
        <li>
          <strong>Du legitimerar dig med BankID</strong> för att skicka in dina
          svar
        </li>
        <li>
          <strong>Du laddar ner appen och loggar in</strong> – dina svar finns
          redan där
        </li>
        <li>
          <strong>Du får en kostnadsfri bedömning</strong>. Du fyller i resten
          av dina svar om din tidigare behandling i appen. Din sjuksköterska går
          igenom dem och återkommer i chatten med en bedömning av om vi kan
          hjälpa dig och vad som är rätt nästa steg.
        </li>
        <li>
          <strong>Om behandling är rätt</strong> för dig gör vi en utredning.{" "}
          <a href="https://blodtrycksdoktorn.se/priser/" target="_blank">
            Kostnad för utredning tillkommer innan påbörjad behandling
          </a>
          .
        </li>
      </ol>

      <p>
        Blodtrycksdoktorn är en registrerad svensk vårdgivare. BankID krävs för
        trygg och säker identifiering.
      </p>
    </>
  );
};

const ExistingTreatmentInfoVariantB = () => {
  return (
    <>
      <h1>Skicka in dina svar</h1>
      <p>
        Tack för dina svar. Nu behöver du bara logga in med BankID för att
        skicka in dem. Blodtrycksdoktorn är en registrerad svensk vårdgivare.
        BankID krävs för trygg och säker identifiering.
      </p>
    </>
  );
};
